import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

/**
 * The scrolling viewport. Only this scrolls -- the card around it clips, so a chart wider
 * than the page can never make the page itself scroll sideways.
 */
export const scrollViewportStyles: SxProps<Theme> = {
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    // A flex/grid child refuses to shrink below its content without this, which is how the
    // chart ends up being handed a negative width.
    minWidth: 0,
    maxWidth: '100%',
    // Swiping sideways must not walk the browser's back gesture or rock the page.
    overscrollBehaviorX: 'contain',
    // x-charts declares `touch-action: pan-y` on its own <svg> so that a chart cannot pan or
    // zoom the page under its pointer handling. `pan-y` permits *only* vertical panning, so
    // on a phone the browser swallowed every horizontal swipe that started on the chart --
    // which is the whole chart -- and the scrollbar underneath is an indicator on touch, not
    // a control, so there was no way left to move the year. Adding pan-x back gives the
    // swipe to this viewport and leaves the rest of MUI's restriction (no pinch-zoom, no
    // double-tap zoom) exactly as it was.
    //
    // Matched on the element, not on `.MuiChartsSurface-root`: that name is only an emotion
    // *label*, so the class in the DOM is the single token `css-<hash>-MuiChartsSurface-root`
    // and a selector for the bare name matches nothing. The only svg under this viewport is
    // the chart.
    '& svg': {
        touchAction: 'pan-x pan-y',
    },
};

/**
 * The chart's own track. Wider than the viewport whenever the months need more room than
 * the card has, which is what turns crowding into a swipe rather than into unreadable
 * 9px bars.
 */
export const scrollTrackStyles = (minWidth: number): SxProps<Theme> => ({
    minWidth,
});

/**
 * Whether this is shown at all is now a measurement, not a breakpoint -- see
 * `useHorizontalOverflow`. It used to carry `display: { xs: 'block', sm: 'none' }`, which
 * hid it at exactly the widths where a roster of any size still scrolls.
 */
export const scrollHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    mt: 0.5,
};
